import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box,
  Typography, TextField, Button, Alert, CircularProgress,
  Chip, Divider, InputAdornment, Paper, Grid,
} from "@mui/material";

import CloseIcon         from "@mui/icons-material/Close";
import PaymentIcon       from "@mui/icons-material/Payment";
import TwoWheelerIcon    from "@mui/icons-material/TwoWheeler";
import PersonIcon        from "@mui/icons-material/Person";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import PhoneIcon         from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon       from "@mui/icons-material/Receipt";
import EditIcon          from "@mui/icons-material/Edit";
import SwapHorizIcon     from "@mui/icons-material/SwapHoriz";

import api from "../service/api";

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: "#888", fontWeight: 700, fontSize: "0.62rem", letterSpacing: 0.6, textTransform: "uppercase" }}
  >
    {children}
  </Typography>
);
const Val = ({ children, color }) => (
  <Typography variant="body2" fontWeight={700} color={color || "#111"} mt={0.2}>
    {children || "—"}
  </Typography>
);
const InfoRow = ({ label, value, color }) => (
  <Box>
    <Label>{label}</Label>
    <Val color={color}>{value}</Val>
  </Box>
);

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const UpdateExchangePaymentModal = ({ open, onClose, bike, onSuccess }) => {

  // ── fetched data ──────────────────────────────────────────────────────────
  const [stockDoc,     setStockDoc]     = useState(null);   // full stock document
  const [saleDoc,      setSaleDoc]      = useState(null);   // ExchangeSellId populated
  const [saleId,       setSaleId]       = useState(null);   // _id of sale for PUT
  const [saleDetail,   setSaleDetail]   = useState(null);   // exchange payment summary
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError,   setFetchError]   = useState("");

  // ── form ──────────────────────────────────────────────────────────────────
  const [form,         setForm]         = useState({ amount: "", date: "", name: "", phone: "" });
  const [submitting,   setSubmitting]   = useState(false);
  const [formError,    setFormError]    = useState("");
  const [success,      setSuccess]      = useState(false);
  const [savedPayment, setSavedPayment] = useState(null);

  // ── fetch when modal opens ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !bike?._id) return;

    const load = async () => {
      try {
        setFetchLoading(true);
        setFetchError("");
        setStockDoc(null);
        setSaleDoc(null);
        setSaleId(null);
        setSaleDetail(null);
        setSuccess(false);
        setFormError("");
        setSavedPayment(null);

        const res  = await api.get(`/specific-bike-details/${bike._id}`);

        // API: { success, message, data: { ...stockFields, ExchangeSellId: { ...saleFields, exchange: {...} } } }
        const d = res.data?.data;
        console.log("UpdateModal fetch:", d);

        // ── Stock document (root level) ────────────────────────────────────
        setStockDoc(d);

        // ── Sale document (populated ExchangeSellId) ───────────────────────
        const sale = d?.ExchangeSellId;
        setSaleDoc(sale || null);
        setSaleId(sale?._id || null);

        // ── Exchange payment summary from sale.exchange ────────────────────
        const exchange = sale?.exchange || null;
        if (exchange) {
          setSaleDetail({
            exchangePrice:   exchange.exchangePrice   || 0,
            paidAmount:      exchange.paidAmount      || 0,
            remainingAmount: exchange.remainingAmount || 0,
            status:          exchange.status          || "",
            payments:        exchange.payments        || [],
          });

          // ── Pre-fill form from first existing payment ──────────────────
          const existing = exchange.payments?.[0];
          setForm({
            amount: existing?.amount ? String(existing.amount) : "",
            date:   existing?.date
              ? new Date(existing.date).toISOString().slice(0, 10)
              : new Date().toISOString().slice(0, 10),
            name:   existing?.name  || "",
            phone:  existing?.phone || "",
          });
        } else {
          // No exchange info — reset form to today
          setForm({ amount: "", date: new Date().toISOString().slice(0, 10), name: "", phone: "" });
        }

      } catch (err) {
        console.error("UpdateModal fetch error:", err);
        setFetchError("Failed to load exchange details. Please try again.");
      } finally {
        setFetchLoading(false);
      }
    };

    load();
  }, [open, bike]);

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");
    if (!form.amount || Number(form.amount) <= 0) { setFormError("Please enter a valid amount.");  return; }
    if (!form.date)                                { setFormError("Payment date is required.");     return; }
    if (!form.name.trim())                         { setFormError("Recipient name is required.");   return; }
    if (!form.phone.trim())                        { setFormError("Phone number is required.");     return; }

    try {
      setSubmitting(true);
      const res = await api.put(`/update-sale-exchange-payment/${saleId}`, {
        amount: Number(form.amount),
        date:   form.date,
        name:   form.name.trim(),
        phone:  form.phone.trim(),
      });
      setSavedPayment(res.data?.data || null);
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Update failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { if (!submitting) onClose(); };

  const existingPayment = saleDetail?.payments?.[0] || null;

  const titleText = success
    ? "Payment Updated!"
    : existingPayment
    ? "Update Exchange Payment"
    : "Record Exchange Payment";

  const titleBg = success
    ? "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)"
    : "linear-gradient(135deg, #bf360c 0%, #e65100 100%)";

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open} onClose={handleClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ── TITLE ── */}
      <DialogTitle sx={{
        fontWeight: 800, background: titleBg, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.8, px: 2.5,
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          {success
            ? <CheckCircleIcon sx={{ fontSize: 20 }} />
            : existingPayment
            ? <EditIcon        sx={{ fontSize: 20 }} />
            : <PaymentIcon     sx={{ fontSize: 20 }} />}
          <Typography fontWeight={800} fontSize="1rem">{titleText}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#fff" }} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>

        {/* ── LOADING ── */}
        {fetchLoading && (
          <Box textAlign="center" py={6}>
            <CircularProgress size={32} sx={{ color: "#e65100" }} />
            <Typography variant="caption" display="block" mt={1.5} color="text.secondary">
              Loading exchange details…
            </Typography>
          </Box>
        )}

        {/* ── FETCH ERROR ── */}
        {!fetchLoading && fetchError && (
          <Box>
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{fetchError}</Alert>
            <Button fullWidth variant="outlined" onClick={handleClose} sx={{ borderRadius: 2 }}>Close</Button>
          </Box>
        )}

        {!fetchLoading && !fetchError && (
          <>
            {/* ══ SECTION 1: Exchange Stock (the bike being given in exchange) ══ */}
            <Paper elevation={0} sx={{
              p: 2, mb: 2, borderRadius: 2,
              border: "1px solid #e0e7ef",
              background: "linear-gradient(135deg, #fff8f5 0%, #fff 100%)",
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <TwoWheelerIcon sx={{ color: "#e65100", fontSize: 18 }} />
                <Typography fontWeight={800} fontSize="0.8rem" color="#e65100" letterSpacing={0.3}>
                  Exchange Vehicle (Stock)
                </Typography>
                <Chip
                  label={stockDoc?.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : stockDoc?.vehicleType || "—"}
                  size="small"
                  sx={{ fontSize: "0.6rem", fontWeight: 700,
                    bgcolor: "#e3f2fd", color: "#1565c0" }}
                />
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={4}><InfoRow label="Company"        value={stockDoc?.companyName} /></Grid>
                <Grid item xs={6} sm={4}><InfoRow label="Model"          value={stockDoc?.model} /></Grid>
                <Grid item xs={6} sm={4}><InfoRow label="Colour"         value={stockDoc?.colour} /></Grid>
                <Grid item xs={6} sm={4}><InfoRow label="Chasis No"      value={stockDoc?.chasis} /></Grid>
                <Grid item xs={6} sm={4}><InfoRow label="Engine No"      value={stockDoc?.engine} /></Grid>
                <Grid item xs={6} sm={4}><InfoRow label="Battery"        value={stockDoc?.batteryType} /></Grid>
                <Grid item xs={6} sm={4}><InfoRow label="Location"       value={stockDoc?.currentLocation} /></Grid>
                <Grid item xs={6} sm={4}>
                  <InfoRow
                    label="Purchase Price"
                    value={stockDoc?.purchasePrice ? `₹${Number(stockDoc.purchasePrice).toLocaleString("en-IN")}` : "—"}
                    color="#2e7d32"
                  />
                </Grid>
                {stockDoc?.registrationNumber && (
                  <Grid item xs={6} sm={4}><InfoRow label="Reg. No" value={stockDoc.registrationNumber} /></Grid>
                )}
                <Grid item xs={6} sm={4}>
                  <Label>Status</Label>
                  <Chip label={stockDoc?.status || "—"} size="small" sx={{
                    mt: 0.3, fontWeight: 700, fontSize: "0.65rem",
                    bgcolor: stockDoc?.status === "IN_STOCK" ? "#e8f5e9" : "#fce4ec",
                    color:   stockDoc?.status === "IN_STOCK" ? "#2e7d32" : "#c62828",
                    border:  `1px solid ${stockDoc?.status === "IN_STOCK" ? "#a5d6a7" : "#ef9a9a"}`,
                  }} />
                </Grid>
              </Grid>
            </Paper>

            {/* ══ SECTION 2: Sale Info (the new vehicle sold to customer) ══ */}
            {saleDoc && (
              <Paper elevation={0} sx={{
                p: 2, mb: 2, borderRadius: 2,
                border: "1px solid #bbdefb",
                background: "linear-gradient(135deg, #e3f2fd 0%, #fff 100%)",
              }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={0.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SwapHorizIcon sx={{ color: "#1565c0", fontSize: 18 }} />
                    <Typography fontWeight={800} fontSize="0.8rem" color="#1565c0" letterSpacing={0.3}>
                      Sale Info
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {saleDoc.invoiceNo && (
                      <Chip label={saleDoc.invoiceNo} size="small"
                        sx={{ fontWeight: 700, fontSize: "0.62rem", bgcolor: "#e3f2fd", color: "#1565c0", border: "1px solid #90caf9" }} />
                    )}
                    {saleDoc.saleType && (
                      <Chip label={saleDoc.saleType} size="small"
                        sx={{ fontWeight: 700, fontSize: "0.62rem", bgcolor: "#fff3e0", color: "#e65100" }} />
                    )}
                  </Box>
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={4}><InfoRow label="Customer"      value={saleDoc.customerName} color="#1565c0" /></Grid>
                  <Grid item xs={6} sm={4}>
                    <InfoRow
                      label="Mobile"
                      value={Array.isArray(saleDoc.mobile) ? saleDoc.mobile.join(", ") : saleDoc.mobile || "—"}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <InfoRow
                      label="Sale Date"
                      value={saleDoc.saleDate ? new Date(saleDoc.saleDate).toLocaleDateString("en-IN") : "—"}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}><InfoRow label="Showroom"      value={saleDoc.location} /></Grid>
                  <Grid item xs={6} sm={4}><InfoRow label="New Model"      value={saleDoc.model} /></Grid>
                  <Grid item xs={6} sm={4}><InfoRow label="Company"        value={saleDoc.companyName} /></Grid>
                  <Grid item xs={6} sm={4}><InfoRow label="Engine No"      value={saleDoc.engineNumber} /></Grid>
                  <Grid item xs={6} sm={4}><InfoRow label="Chasis No"      value={saleDoc.chasisNumber} /></Grid>
                  <Grid item xs={6} sm={4}>
                    <InfoRow
                      label="Sale Price"
                      value={saleDoc.salePrice ? `₹${Number(saleDoc.salePrice).toLocaleString("en-IN")}` : "—"}
                      color="#1b5e20"
                    />
                  </Grid>
                  {saleDoc.cashAmount > 0 && (
                    <Grid item xs={6} sm={4}>
                      <InfoRow
                        label="Cash Amount"
                        value={`₹${Number(saleDoc.cashAmount).toLocaleString("en-IN")}`}
                        color="#1565c0"
                      />
                    </Grid>
                  )}
                </Grid>

                {/* Exchange vehicle info from sale.exchange */}
                {saleDoc.exchange && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" fontWeight={700} color="#666" letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                      Exchange Vehicle (from sale)
                    </Typography>
                    <Grid container spacing={1}>
                      {saleDoc.exchange.ownerName && (
                        <Grid item xs={6} sm={4}><InfoRow label="Owner"  value={saleDoc.exchange.ownerName} /></Grid>
                      )}
                      {saleDoc.exchange.phone && (
                        <Grid item xs={6} sm={4}><InfoRow label="Phone"  value={saleDoc.exchange.phone} /></Grid>
                      )}
                      {saleDoc.exchange.model && (
                        <Grid item xs={6} sm={4}><InfoRow label="Model"  value={saleDoc.exchange.model} /></Grid>
                      )}
                      {saleDoc.exchange.registrationNumber && (
                        <Grid item xs={6} sm={4}><InfoRow label="Reg No" value={saleDoc.exchange.registrationNumber} /></Grid>
                      )}
                      {saleDoc.exchange.location && (
                        <Grid item xs={6} sm={4}><InfoRow label="Location" value={saleDoc.exchange.location} /></Grid>
                      )}
                    </Grid>
                  </>
                )}
              </Paper>
            )}

            {/* ══ SECTION 3: Exchange Payment Summary banner ══ */}
            {saleDetail && (
              <Paper elevation={0} sx={{
                p: 2, mb: 2, borderRadius: 2,
                bgcolor: "#fff3e0", border: "1px solid #ffb74d",
              }}>
                <Typography variant="caption" fontWeight={700} color="#e65100" letterSpacing={0.5} textTransform="uppercase" display="block" mb={1}>
                  Exchange Payment Summary
                </Typography>
                <Grid container spacing={1} textAlign="center">
                  <Grid item xs={4}>
                    <Typography variant="caption" color="#888" fontWeight={700} textTransform="uppercase" fontSize="0.6rem" display="block">
                      Agreed
                    </Typography>
                    <Typography variant="h6" fontWeight={900} color="#e65100" lineHeight={1.2}>
                      ₹{(saleDetail.exchangePrice || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="#888" fontWeight={700} textTransform="uppercase" fontSize="0.6rem" display="block">
                      Paid
                    </Typography>
                    <Typography variant="h6" fontWeight={900} color="#2e7d32" lineHeight={1.2}>
                      ₹{(saleDetail.paidAmount || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="#888" fontWeight={700} textTransform="uppercase" fontSize="0.6rem" display="block">
                      Remaining
                    </Typography>
                    <Typography variant="h6" fontWeight={900} lineHeight={1.2}
                      color={saleDetail.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>
                      ₹{(saleDetail.remainingAmount || 0).toLocaleString("en-IN")}
                    </Typography>
                  </Grid>
                </Grid>
                <Box textAlign="center" mt={1}>
                  <Chip
                    label={saleDetail.status || "—"}
                    size="small"
                    sx={{
                      fontWeight: 800, fontSize: "0.68rem",
                      bgcolor: saleDetail.status === "PAID" ? "#e8f5e9" : "#fce4ec",
                      color:   saleDetail.status === "PAID" ? "#2e7d32" : "#c62828",
                      border:  `1px solid ${saleDetail.status === "PAID" ? "#a5d6a7" : "#ef9a9a"}`,
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* ══ SUCCESS STATE ══════════════════════════════════════════════ */}
            {success && (
              <>
                <Paper elevation={0} sx={{
                  p: 2.5, mb: 2, borderRadius: 2, textAlign: "center",
                  bgcolor: "#e8f5e9", border: "1px solid #a5d6a7",
                }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 44 }} />
                  <Typography fontWeight={800} color="#2e7d32" mt={0.8} fontSize="1rem">
                    Payment Updated!
                  </Typography>
                  <Typography variant="caption" color="#555">
                    Exchange payment has been recorded successfully.
                  </Typography>
                </Paper>

                {savedPayment && (
                  <Paper elevation={0} sx={{
                    p: 2, mb: 2, borderRadius: 2,
                    border: "1px solid #dcedc8", bgcolor: "#f9fbe7",
                  }}>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                      <ReceiptIcon sx={{ color: "#2e7d32", fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={700} color="#2e7d32"
                        letterSpacing={0.5} textTransform="uppercase">
                        Updated Receipt
                      </Typography>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Label>Exchange Price</Label>
                        <Val color="#333">₹{(savedPayment.exchangePrice || 0).toLocaleString("en-IN")}</Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Amount Paid</Label>
                        <Val color="#2e7d32">₹{(savedPayment.paidAmount || 0).toLocaleString("en-IN")}</Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Remaining</Label>
                        <Val color={savedPayment.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>
                          ₹{(savedPayment.remainingAmount || 0).toLocaleString("en-IN")}
                        </Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Status</Label>
                        <Chip label={savedPayment.status || "—"} size="small" sx={{
                          mt: 0.3, fontWeight: 800, fontSize: "0.65rem",
                          bgcolor: savedPayment.status === "PAID" ? "#e8f5e9" : "#fff3e0",
                          color:   savedPayment.status === "PAID" ? "#2e7d32" : "#e65100",
                          border:  `1px solid ${savedPayment.status === "PAID" ? "#a5d6a7" : "#ffb74d"}`,
                        }} />
                      </Grid>
                      {savedPayment.invoiceNo && (
                        <Grid item xs={12}><Label>Invoice</Label><Val>{savedPayment.invoiceNo}</Val></Grid>
                      )}
                      {savedPayment.payment && (
                        <>
                          <Grid item xs={6}><Label>Recipient</Label><Val>{savedPayment.payment.name}</Val></Grid>
                          <Grid item xs={6}><Label>Phone</Label><Val>{savedPayment.payment.phone}</Val></Grid>
                          <Grid item xs={6}>
                            <Label>Payment Date</Label>
                            <Val>
                              {savedPayment.payment.date
                                ? new Date(savedPayment.payment.date).toLocaleDateString("en-IN")
                                : "—"}
                            </Val>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Paper>
                )}

                <Button fullWidth variant="contained" onClick={handleClose}
                  sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}>
                  Done
                </Button>
              </>
            )}

            {/* ══ FORM ══════════════════════════════════════════════════════ */}
            {!success && (
              <>
                {/* Show existing payment record if any */}
                {existingPayment && (
                  <>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1}>
                      <ReceiptIcon sx={{ color: "#888", fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={700} color="#888"
                        letterSpacing={0.5} textTransform="uppercase">
                        Current Payment Record
                      </Typography>
                    </Box>
                    <Paper elevation={0} sx={{
                      p: 1.8, mb: 2, borderRadius: 2,
                      border: "1px solid #dcedc8", bgcolor: "#f9fbe7",
                    }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Label>Amount</Label>
                          <Typography variant="body1" fontWeight={900} color="#2e7d32">
                            ₹{(existingPayment.amount || 0).toLocaleString("en-IN")}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Label>Date</Label>
                          <Val>
                            {existingPayment.date
                              ? new Date(existingPayment.date).toLocaleDateString("en-IN")
                              : "—"}
                          </Val>
                        </Grid>
                        {existingPayment.name  && <Grid item xs={6}><Label>Recipient</Label><Val>{existingPayment.name}</Val></Grid>}
                        {existingPayment.phone && <Grid item xs={6}><Label>Phone</Label><Val>{existingPayment.phone}</Val></Grid>}
                      </Grid>
                    </Paper>
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontSize: "0.74rem", py: 0.5 }}>
                      ⚠️ Submitting will <strong>overwrite</strong> the existing payment record.
                    </Alert>
                  </>
                )}

                <Divider sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="#888" letterSpacing={0.5}>
                    {existingPayment ? "UPDATE PAYMENT" : "RECORD PAYMENT"}
                  </Typography>
                </Divider>

                {/* Form fields — pre-filled from existing payment */}
                <Box display="flex" flexDirection="column" gap={1.8}>
                  <TextField
                    label="Payment Amount (₹)" type="number" size="small" fullWidth
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    inputProps={{ min: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon sx={{ fontSize: 15, color: "#e65100" }} />
                        </InputAdornment>
                      )
                    }}
                    helperText={
                      saleDetail?.exchangePrice
                        ? `Agreed exchange value: ₹${saleDetail.exchangePrice.toLocaleString("en-IN")}`
                        : undefined
                    }
                  />
                  <TextField
                    label="Payment Date" type="date" size="small" fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.date}
                    onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon sx={{ fontSize: 14, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    label="Recipient Name" size="small" fullWidth
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ fontSize: 15, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    label="Phone Number" size="small" fullWidth
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ fontSize: 15, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>

                {formError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontSize: "0.8rem" }}>
                    {formError}
                  </Alert>
                )}

                <Box display="flex" gap={1.5} mt={2.5}>
                  <Button fullWidth variant="outlined" onClick={handleClose} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Cancel
                  </Button>
                  <Button fullWidth variant="contained" onClick={handleSubmit} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}>
                    {submitting ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                        Saving…
                      </Box>
                    ) : existingPayment ? "✅ Update Payment" : "✅ Record Payment"}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpdateExchangePaymentModal;